import React from 'react'

import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { useIsStale } from '@libs/typescript-utils'

import { BUTTON_CSS_VARIABLES, ButtonWithIcon, Typography } from '@components/ui'
import { InfoIcon, RotateRightIcon } from '@components/ui/svgIcons'

import { generateMaxWidthBreakPoint, NEW_VERSION_BAR_HEIGHT_PROPERTY } from '@constants'

const BarDiv = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.newVersionBar};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.colors.blue[40]};
  color: ${({ theme }) => theme.colors.gray[0]};
`

const MessageDiv = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const IconSpan = styled.span`
  display: flex;
  flex-shrink: 0;
  padding-top: 1px;
`

const RefreshButton = styled(ButtonWithIcon)`
  ${BUTTON_CSS_VARIABLES.backgroundColor}: ${({ theme }) => theme.colors.gray[0]};
  ${BUTTON_CSS_VARIABLES.borderColor}: ${({ theme }) => theme.colors.gray[0]};
  ${BUTTON_CSS_VARIABLES.color}: ${({ theme }) => theme.colors.blue[40]};
  ${BUTTON_CSS_VARIABLES.hoverBackgroundColor}: ${({ theme }) => theme.colors.secondary[10]};
  ${BUTTON_CSS_VARIABLES.hoverBorderColor}: ${({ theme }) => theme.colors.secondary[10]};
  ${BUTTON_CSS_VARIABLES.activeBackgroundColor}: ${({ theme }) => theme.colors.secondary[20]};
  ${BUTTON_CSS_VARIABLES.focusBorderColor}: ${({ theme }) => theme.colors.common.outlineBlue};

  ${generateMaxWidthBreakPoint('mobile')} {
    width: 100%;
    height: 44px;
  }
`

function handleRefresh() {
  window.location.reload()
}

function VisibleNewVersionBar() {
  const barRef = React.useRef<HTMLDivElement>(null)

  // Fixed elements and viewport-height layouts read this property to make room for the bar.
  React.useLayoutEffect(() => {
    const bar = barRef.current
    if (!bar) {
      return undefined
    }
    const rootStyle = document.documentElement.style
    // Rounded up so a fractional bar height can't leave the bar overlapping the header by a pixel.
    const updateHeight = () =>
      rootStyle.setProperty(NEW_VERSION_BAR_HEIGHT_PROPERTY, `${Math.ceil(bar.getBoundingClientRect().height)}px`)
    updateHeight()
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateHeight)
    resizeObserver?.observe(bar)
    return () => {
      resizeObserver?.disconnect()
      rootStyle.removeProperty(NEW_VERSION_BAR_HEIGHT_PROPERTY)
    }
  }, [])

  return (
    <BarDiv ref={barRef} role="status">
      <MessageDiv>
        <IconSpan aria-hidden="true">
          <InfoIcon size={18} />
        </IconSpan>
        <Typography size="400-sm" color="white">
          <FormattedMessage
            id="newVersionBar.text"
            defaultMessage="Elérhető az alkalmazás új verziója. A következő oldalváltáskor automatikusan frissül."
          />
        </Typography>
      </MessageDiv>
      <RefreshButton size="small" icon={<RotateRightIcon size={14} />} onClick={handleRefresh}>
        <FormattedMessage id="newVersionBar.refreshButton" defaultMessage="Frissítés most" />
      </RefreshButton>
    </BarDiv>
  )
}

/** Tells a stale tab that a new version is out; the next page change loads it. */
export function NewVersionBar() {
  const stale = useIsStale()
  return stale ? <VisibleNewVersionBar /> : null
}
